import React from "react";
import {
  MASK_BAR_COLOR,
  MASK_BAR_RADIUS,
  MASK_LOCK_GLYPH,
  MASK_SCALE,
  maskBarWidths,
  type MaskScale,
} from "@semo/shared-types/constants";
import { cn } from "../utils/cn";

interface MaskedSectionProps {
  /** 백엔드가 내려준 익명 여부. false 면 children 을 그대로 렌더한다. */
  isMasked?: boolean;
  /** 스켈레톤 위에 얹을 안내 문구. 보는 쪽에 따라 주어가 달라지므로 호출부에서 넘긴다. */
  message: React.ReactNode;
  /** 컨테이너 폭에 맞춘 스케일 */
  scale?: MaskScale;
  /** 스켈레톤 바 개수. 보통 가려지는 필드 수와 맞춘다. */
  barCount?: number;
  /** 자물쇠 노출 여부 */
  showLock?: boolean;
  className?: string;
  children: React.ReactNode;
}

function LockGlyph({ width, height }: { width: number; height: number }) {
  const g = MASK_LOCK_GLYPH;

  return (
    <svg
      width={width}
      height={height}
      viewBox={g.viewBox}
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        d={g.shackle}
        stroke="currentColor"
        strokeWidth={g.shackleWidth}
        strokeLinecap="round"
      />
      <rect {...g.body} fill="currentColor" />
      {/* 열쇠구멍은 몸통을 파내는 대신 흰색으로 덮는다. 바 위에 얹혀도 형태가 유지된다. */}
      <circle {...g.keyholeCircle} fill="#FFFFFF" />
      <rect {...g.keyholeStem} fill="#FFFFFF" />
    </svg>
  );
}

/**
 * 계약 체결 전 상대방 정보를 가리는 스켈레톤 블록.
 *
 * 값 하나씩 흐리던 기존 BlurredContent 와 달리 섹션 전체를 대체한다.
 * `isMasked` 일 때 children 을 아예 렌더하지 않으므로 DOM 에 마스킹된
 * 자리표시 값(`***`)조차 남지 않는다.
 *
 * 안내 문구는 hover 없이 상시 노출된다. 기존 툴팁 방식은 마우스를 올려야만
 * 이유를 알 수 있었고 모바일에서는 아예 뜨지 않았다.
 */
export function MaskedSection({
  isMasked = true,
  message,
  scale = "document",
  barCount = 4,
  showLock = true,
  className = "",
  children,
}: MaskedSectionProps) {
  if (!isMasked) {
    return <>{children}</>;
  }

  const s = MASK_SCALE[scale];
  const widths = maskBarWidths(barCount);

  return (
    <div className={cn("relative w-full", className)}>
      {/* 스켈레톤 바 — 장식이므로 보조기기에서 숨긴다 */}
      <div
        aria-hidden="true"
        className="flex flex-col"
        style={{
          gap: s.gap,
          paddingLeft: s.paddingX,
          paddingRight: s.paddingX,
          paddingTop: s.paddingY,
          paddingBottom: s.paddingY,
        }}
      >
        {widths.map((width, i) => (
          <div
            key={i}
            style={{
              width,
              height: s.barHeight,
              background: MASK_BAR_COLOR,
              borderRadius: MASK_BAR_RADIUS,
            }}
          />
        ))}
      </div>

      {/*
        오버레이는 바 위에 겹친다 (의도된 레이어링).
        원본 디자인의 left:250 / top:107 은 중앙정렬을 절대좌표로 굳힌 값이라
        컨테이너 폭이 달라져도 따라오도록 inset-0 + center 로 구현한다.
      */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
        style={{ gap: s.lockGap, color: s.textColor }}
      >
        {showLock && <LockGlyph width={s.lockWidth} height={s.lockHeight} />}
        <p
          style={{
            maxWidth: s.maxTextWidth,
            fontSize: s.fontSize,
            lineHeight: s.lineHeight,
            fontWeight: 500,
          }}
        >
          {message}
        </p>
      </div>
    </div>
  );
}
